import { Component } from '@theme/component';
import './rheve-size-guide.js';

const normalizedText = (element) => element.textContent.replace(/\s+/g, ' ').trim().toLowerCase();

// Read only a single, explicit top-level source section. Ambiguous content stays in Product Details.
export function findDescriptionSection(root, titles) {
  const headings = [...root.children].filter(
    (element) => /^H[1-6]$/.test(element.tagName) && titles.includes(normalizedText(element))
  );
  if (headings.length !== 1) return null;

  const heading = headings[0];
  const level = Number(heading.tagName.slice(1));
  const nodes = [heading];
  for (let node = heading.nextSibling; node; node = node.nextSibling) {
    if (/^H[1-6]$/.test(node.tagName || '') && Number(node.tagName.slice(1)) <= level) break;
    nodes.push(node);
  }
  return { heading, nodes };
}

class RheveProductDescription extends Component {
  #moved = [];
  #guide;

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.part === 'details') this.#enhance();
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.dataset.part === 'details') this.#enhance();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#restore();
  }

  #restore() {
    this.#moved.forEach(({ marker, nodes }) => {
      if (marker.parentNode) marker.replaceWith(...nodes);
    });
    this.#moved = [];
    // Once original nodes return to their source, the existing helper clears its empty controls/panels.
    this.#guide?.updatedCallback();
    this.#guide = null;
  }

  #move(section, target) {
    const marker = document.createComment('rheve-product-description-source');
    section.heading.before(marker);
    this.#moved.push({ marker, nodes: section.nodes });
    if (target) target.append(...section.nodes);
    else section.nodes.forEach((node) => node.remove());
  }

  #enhance() {
    this.#restore();
    const source = this.querySelector('[data-rheve-product-copy-source]');
    const copyRoot = this.closest('[data-rheve-product-copy-root]') || this.closest('.accordion');
    if (!source || !copyRoot) return;

    const intro = copyRoot.querySelector('rheve-product-description[data-part="intro"]');
    const measurements = copyRoot.querySelector('rheve-product-description[data-part="measurements"]');
    const packaging = copyRoot.querySelector('rheve-product-description[data-part="packaging"]');
    intro?.removeAttribute('data-ready');
    measurements?.removeAttribute('data-ready');
    packaging?.removeAttribute('data-ready');

    const introTarget = intro?.querySelector('[data-rheve-copy-target]');
    const firstHeading = [...source.children].find((node) => /^H[1-6]$/.test(node.tagName));
    if (introTarget && firstHeading) {
      const nodes = [];
      for (let node = source.firstChild; node && node !== firstHeading; node = node.nextSibling) nodes.push(node);
      if (nodes.some((node) => node.textContent.trim())) {
        this.#move({ heading: nodes[0], nodes }, introTarget);
        intro.setAttribute('data-ready', '');
      }
    }

    const guide = findDescriptionSection(source, ['size guide', 'measurements']);
    const measurementTarget = measurements?.querySelector('[data-rheve-copy-target]');
    if (guide && measurementTarget && guide.nodes.some((node) => node.tagName === 'TABLE' || node.querySelector?.('table'))) {
      this.#move(guide, measurementTarget);
      measurements.setAttribute('data-ready', '');
      this.#guide = measurements.querySelector('rheve-size-guide');
      this.#guide?.updatedCallback();
    }

    const packageSection = findDescriptionSection(source, ['packaging']);
    const packagingTarget = packaging?.querySelector('[data-rheve-copy-target]');
    if (packageSection && packagingTarget && packageSection.nodes.slice(1).some((node) => node.textContent.trim())) {
      this.#move(packageSection, packagingTarget);
      packaging.setAttribute('data-ready', '');
    }

    const careSection = findDescriptionSection(source, ['care', 'care instructions']);
    const careRow = [...copyRoot.querySelectorAll('accordion-custom')].find(
      (row) => normalizedText(row.querySelector('summary') || row) === 'care'
    );
    const displayedCare = careRow?.querySelector('.details-content');
    const sourceCare = careSection?.nodes.slice(1).map((node) => node.textContent).join('').replace(/\s+/g, ' ').trim().toLowerCase();
    if (careSection && displayedCare && sourceCare === normalizedText(displayedCare)) this.#move(careSection);
  }
}

if (!customElements.get('rheve-product-description')) {
  customElements.define('rheve-product-description', RheveProductDescription);
}
